// File: src/pages/HR/ManagerMissedPunchApproval.js
/*
 * =====================================================================
 * WHAT CHANGED vs ORIGINAL:
 * -----------------------------------------------------------------------
 * ORIGINAL:
 *   - filter: r.status === 'PENDING' — this misses PENDING_MANAGER status
 *   - No approval manager remark column
 *   - No approval manager name shown
 *   - Table title: "Missed Punch Approvals (Manager)"
 *
 * NEW (ALL CHANGES MARKED // NEW):
 *   - filter: includes both 'PENDING' and 'PENDING_MANAGER'
 *     so records escalated from AM (24h) also show up here
 *   - NEW column: "AM Remark (View Only)" — shows approvalManagerRemarks
 *     as a read-only yellow badge with tooltip of full text
 *   - NEW column: shows if record was escalated (had AM stage)
 *   - Status chip: PENDING_MANAGER shows in orange, PENDING in warning
 * =====================================================================
 */
import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableHead, TableRow,
    TableCell, TableBody, Button, TextField, Stack, Chip,
    TableContainer, Tooltip
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const ManagerMissedPunchApproval = () => {
    const [requests, setRequests] = useState([]);
    const [remarks, setRemarks] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const loadRequests = async () => {
        try {
            setLoading(true);
            setError('');
            const res = await api.get('/missed-punch/manager');
            setRequests(res.data || []);
        } catch (err) {
            setError('Failed to load missed punch approvals');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadRequests(); }, []);

    const approve = async (id) => {
        try {
            await api.post(`/missed-punch/manager/approve/${id}?remarks=${encodeURIComponent(remarks[id] || '')}`);
            toast.success('Approved');
            loadRequests();
        } catch (err) {
            toast.error('Approve failed: ' + (err?.response?.data?.message || err.message));
        }
    };

    const reject = async (id) => {
        if (!remarks[id]?.trim()) {
            toast.error('Remarks required to reject');
            return;
        }
        try {
            await api.post(`/missed-punch/manager/reject/${id}?remarks=${encodeURIComponent(remarks[id] || '')}`);
            toast.success('Rejected');
            loadRequests();
        } catch (err) {
            toast.error('Reject failed: ' + (err?.response?.data?.message || err.message));
        }
    };

    // NEW: Include both PENDING and PENDING_MANAGER (records escalated from AM also show up)
    const pendingRequests = requests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'PENDING' || s === 'PENDING_MANAGER';
    });

    return (
        <Box sx={{ p: 2, width: '100%' }}>
            <Typography variant="h5" fontWeight={800} mb={3}>
                Missed Punch Approvals (Cluster Manager)
            </Typography>

            {error && (
                <Paper sx={{ p: 2, mb: 2, backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 2 }}>
                    {error}
                </Paper>
            )}

            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
                        <TableRow>
                            <TableCell><b>Employee</b></TableCell>
                            <TableCell><b>Date</b></TableCell>
                            <TableCell><b>Punch Type</b></TableCell>
                            <TableCell><b>Clock In</b></TableCell>
                            <TableCell><b>Clock Out</b></TableCell>
                            <TableCell><b>Reason</b></TableCell>
                            {/* NEW column */}
                            <TableCell><b>AM Remark</b> <Typography component="span" fontSize={10} color="text.secondary">(View Only)</Typography></TableCell>
                            <TableCell><b>Status</b></TableCell>
                            <TableCell><b>Your Remarks</b></TableCell>
                            <TableCell><b>Action</b></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">Loading...</TableCell>
                            </TableRow>
                        ) : pendingRequests.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">No pending requests</TableCell>
                            </TableRow>
                        ) : (
                            pendingRequests.map((r) => (
                                <TableRow key={r.attendanceId || r.id} hover>
                                    <TableCell><b>{r.employeeName}</b></TableCell>
                                    <TableCell>{r.date || r.attendanceDate}</TableCell>
                                    <TableCell>
                                        <Chip label={r.punchType || '-'} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>{r.requestedClockIn || '-'}</TableCell>
                                    <TableCell>{r.requestedClockOut || '-'}</TableCell>
                                    <TableCell sx={{ maxWidth: 140 }}>
                                        <Typography fontSize={12}>{r.reason}</Typography>
                                    </TableCell>

                                    {/* NEW: AM Remark VIEW ONLY */}
                                    <TableCell sx={{ maxWidth: 160 }}>
                                        {r.approvalManagerRemarks ? (
                                            <Tooltip title={r.approvalManagerRemarks} arrow placement="top">
                                                <Box>
                                                    <Chip
                                                        size="small"
                                                        icon={<InfoOutlined sx={{ fontSize: 13 }} />}
                                                        label="View Note"
                                                        sx={{
                                                            bgcolor: '#fef9c3',
                                                            color: '#854d0e',
                                                            border: '1px solid #fde047',
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                            cursor: 'help'
                                                        }}
                                                    />
                                                    <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.3 }} noWrap>
                                                        {r.approvalManagerRemarks}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Typography fontSize={12} color="text.disabled">—</Typography>
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        <Chip
                                            label={r.status === 'PENDING_MANAGER' ? 'Pending Mgr' : r.status}
                                            size="small"
                                            color="warning"
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <TextField
                                            size="small"
                                            placeholder="Remarks"
                                            sx={{ width: 150 }}
                                            onChange={(e) =>
                                                setRemarks(prev => ({
                                                    ...prev,
                                                    [r.attendanceId || r.id]: e.target.value
                                                }))
                                            }
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Button variant="contained" color="success" size="small"
                                                onClick={() => approve(r.attendanceId || r.id)}>
                                                Approve
                                            </Button>
                                            <Button variant="contained" color="error" size="small"
                                                onClick={() => reject(r.attendanceId || r.id)}>
                                                Reject
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ManagerMissedPunchApproval;